import { isRecord } from 'src/modules/shared/utils/types.util';
import { getRecordValue } from 'src/modules/shared/utils/record.util';
import React from 'react';
import { useNavigate } from '@tanstack/react-router';
import type { WorkflowMetadata } from 'src/api/generated';
import { Route as WorkflowDetailRoute } from 'src/routes/tools/workflows/$workflowId.index';
import { useWorkflowVersion } from 'src/modules/shared/hooks/api/automations/workflows/version';
import { useUpdateWorkflowMutation } from 'src/modules/shared/hooks/api/automations/workflows/update';
import { useAutomationsWorkflows } from 'src/modules/shared/hooks/api/automations/workflows/workflows';
import type { WorkflowEditorMetadataDraft } from 'src/modules/tools/types/workflow-editor.types';
import type {
  WorkflowDefinitionDraft,
  WorkflowVersionDefinition,
} from 'src/modules/tools/utils/workflow-definition.type';

/** Use Workflow Edit Params contract. */
interface UseWorkflowEditParams {
  /** Identifier of the workflow being viewed or edited. */
  workflowId: string;
  /** Current editable workflow definition. */
  definitionDraft?: WorkflowDefinitionDraft;
  /** Current editable workflow input schema. */
  inputSchemaDraft: Record<string, unknown>;
  /** Current editable workflow metadata. */
  metadataDraft: WorkflowEditorMetadataDraft;
  /** Validation failures that prevent saving. */
  inputValidationErrors: string[];
  /** Whether the draft differs from the saved baseline. */
  hasUnsavedChanges: boolean;
  /** Schema used when no input schema has been provided. */
  defaultInputSchema: Record<string, unknown>;
  /** Handles a successfully saved workflow. */
  onSaveSuccess: () => void;
  /** Updates the editable workflow definition. */
  setDefinitionDraft: React.Dispatch<
    React.SetStateAction<WorkflowDefinitionDraft | undefined>
  >;
  /** Updates the editable workflow input schema. */
  setInputSchemaDraft: React.Dispatch<
    React.SetStateAction<Record<string, unknown>>
  >;
  /** Updates the editable workflow metadata. */
  setMetadataDraft: React.Dispatch<
    React.SetStateAction<WorkflowEditorMetadataDraft>
  >;
}

/** Use Workflow Edit Result contract. */
interface UseWorkflowEditResult {
  /** Loaded workflow version being edited. */
  workflowVersion: ReturnType<typeof useWorkflowVersion>['workflowVersion'];
  /** Loaded metadata for the workflow. */
  workflowMeta?: WorkflowMetadata;
  /** Human-readable time of the latest workflow update. */
  formattedUpdatedAt: string | null;
  /** Whether the content is currently loading. */
  loading: boolean;
  /** Error state to display instead of the normal content. */
  error: Error | null;
  /** Error displayed when the workflow cannot be saved. */
  formError: string | null;
  /** Returns to the preceding workflow view. */
  handleBack: () => void;
  /** Validates and saves the current workflow draft. */
  handleSave: () => Promise<void>;
  /** Whether a save request is in progress. */
  isSaving: boolean;
}

export const useWorkflowEdit = ({
  workflowId,
  definitionDraft,
  inputSchemaDraft,
  metadataDraft,
  inputValidationErrors,
  hasUnsavedChanges,
  defaultInputSchema,
  onSaveSuccess,
  setDefinitionDraft,
  setInputSchemaDraft,
  setMetadataDraft,
}: UseWorkflowEditParams): UseWorkflowEditResult => {
  const navigate = useNavigate();
  const { workflowVersion, loading, error } = useWorkflowVersion(
    workflowId,
    'DRAFT',
    true
  );
  const { workflows } = useAutomationsWorkflows(100, true);
  const updateMutation = useUpdateWorkflowMutation();
  const [formError, setFormError] = React.useState<string | null>(null);
  const [shouldNavigateAfterSave, setShouldNavigateAfterSave] =
    React.useState(false);

  const workflowMeta = React.useMemo(
    () => workflows.find((w: WorkflowMetadata) => w.workflowId === workflowId),
    [workflows, workflowId]
  );

  React.useEffect(() => {
    const incomingDef =
      (workflowVersion?.definition as WorkflowDefinitionDraft | undefined) ??
      undefined;
    if (incomingDef) {
      setDefinitionDraft({
        ...incomingDef,
        steps: incomingDef.steps ?? {},
      });
      setInputSchemaDraft(incomingDef.inputSchema ?? defaultInputSchema);
    } else {
      setDefinitionDraft(undefined);
      setInputSchemaDraft(defaultInputSchema);
    }
    const meta =
      workflowVersion && 'metadata' in workflowVersion
        ? workflowVersion.metadata
        : undefined;
    const title =
      isRecord(meta) && typeof meta.title === 'string' ? meta.title : undefined;
    const name =
      isRecord(meta) && typeof meta.name === 'string' ? meta.name : undefined;
    const description =
      isRecord(meta) && typeof meta.description === 'string'
        ? meta.description
        : undefined;
    setMetadataDraft({
      title: title ?? name ?? workflowMeta?.name ?? '',
      description: description ?? workflowMeta?.description ?? '',
    });
  }, [
    defaultInputSchema,
    setDefinitionDraft,
    setInputSchemaDraft,
    setMetadataDraft,
    workflowMeta?.description,
    workflowMeta?.name,
    workflowVersion,
    workflowId,
  ]);

  React.useEffect(() => {
    if (!shouldNavigateAfterSave || hasUnsavedChanges) {
      return;
    }

    void navigate({
      to: WorkflowDetailRoute.to,
      params: { workflowId },
      search: { phoneVersion: 'v1' },
    });
    setShouldNavigateAfterSave(false);
  }, [hasUnsavedChanges, navigate, shouldNavigateAfterSave, workflowId]);

  const formattedUpdatedAt = React.useMemo(() => {
    if (!workflowVersion?.updatedAt) return null;
    try {
      return new Date(workflowVersion.updatedAt).toLocaleString();
    } catch {
      return workflowVersion.updatedAt;
    }
  }, [workflowVersion?.updatedAt]);

  const handleBack = React.useCallback(() => {
    void navigate({
      to: WorkflowDetailRoute.to,
      params: { workflowId },
      search: { phoneVersion: 'v1' },
    });
  }, [navigate, workflowId]);

  const handleSave = React.useCallback(async () => {
    if (!definitionDraft) {
      setFormError('No hay definición disponible para guardar.');
      return;
    }
    const steps = definitionDraft.steps ?? {};
    const finalStep = definitionDraft.finalStep;
    const hasAnyStep =
      Object.keys(steps).length > 0 || Boolean(definitionDraft.finalStep);
    if (!hasAnyStep) {
      setFormError('Agrega al menos un paso al flujo antes de guardar.');
      return;
    }
    const initialStepId = definitionDraft.initialStepId;
    if (!initialStepId) {
      setFormError(
        'Selecciona un paso inicial conectando el nodo de inicio con un paso.'
      );
      return;
    }
    if (
      !getRecordValue(steps, initialStepId) &&
      finalStep?.id !== initialStepId
    ) {
      setFormError('El paso inicial debe existir en el flujo.');
      return;
    }
    if (inputValidationErrors.length > 0) {
      setFormError('Corrige los errores en las entradas antes de guardar.');
      return;
    }
    const definitionPayload: WorkflowVersionDefinition = {
      inputSchema: inputSchemaDraft,
      outputSchema: definitionDraft.outputSchema ?? {},
      inputParams: definitionDraft.inputParams,
      initialStepId,
      steps,
      ...(finalStep ? { finalStep } : {}),
    };
    setFormError(null);
    try {
      const cleanDefinition: WorkflowVersionDefinition =
        structuredClone(definitionPayload);
      const metadataPayload = {
        name: metadataDraft.title,
        description: metadataDraft.description,
      };
      const cleanMetadata = structuredClone(metadataPayload);
      // Debug log to verify payload matches backend schema expectations.
      console.debug('Saving workflow', {
        workflowId,
        definition: cleanDefinition,
        metadata: cleanMetadata,
      });
      await updateMutation.mutateAsync({
        workflowId,
        body: {
          definition: cleanDefinition,
          metadata: cleanMetadata,
        },
      });
      onSaveSuccess();
      setShouldNavigateAfterSave(true);
    } catch (mutationError) {
      setFormError(
        mutationError instanceof Error
          ? mutationError.message
          : 'Error al guardar el flujo.'
      );
    }
  }, [
    definitionDraft,
    inputSchemaDraft,
    inputValidationErrors.length,
    metadataDraft.description,
    metadataDraft.title,
    onSaveSuccess,
    updateMutation,
    workflowId,
  ]);

  return {
    workflowVersion,
    workflowMeta,
    formattedUpdatedAt,
    loading,
    error,
    formError,
    handleBack,
    handleSave,
    isSaving: updateMutation.isPending,
  };
};
